<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  availableFields: {
    type: Array,
    default: () => [],
  },
  selectedFields: {
    type: Array,
    default: () => [],
  },
  maxFields: {
    type: Number,
    default: 20,
  },
});

const emit = defineEmits(['add', 'batch-add']);

const { t } = useI18n();

const showDrawer = ref(false);
const searchQuery = ref('');
const tempSelectedKeys = ref(new Set());

// 假数据（用于无字段可添加时的引导页）
const placeholderFields = [
  {
    attribute_key: 'company',
    attribute_display_name: '公司',
    attribute_description: '客户所在公司',
    attribute_display_type: 'text',
  },
  {
    attribute_key: 'address',
    attribute_display_name: '地址',
    attribute_description: '客户地址',
    attribute_display_type: 'text',
  },
  {
    attribute_key: 'birthday',
    attribute_display_name: '生日',
    attribute_description: '客户生日',
    attribute_display_type: 'date',
  },
];

// 是否没有更多字段可添加（非达到上限的情况）
const hasNoMoreFields = computed(() => {
  return (
    !searchQuery.value && unaddedFields.value.length === 0 && !isAtLimit.value
  );
});

// 图标映射
const iconByType = {
  text: 'i-lucide-align-justify',
  checkbox: 'i-lucide-circle-check-big',
  list: 'i-lucide-list',
  date: 'i-lucide-calendar',
  link: 'i-lucide-link',
  number: 'i-lucide-hash',
};

const getTypeIcon = type => {
  const typeKey = type?.toLowerCase();
  return iconByType[typeKey] || 'i-lucide-align-justify';
};

// 已添加的字段 key 集合
const addedKeys = computed(() => {
  return new Set(props.selectedFields.map(f => f.attribute_key));
});

// 未添加的字段
const unaddedFields = computed(() => {
  return props.availableFields.filter(
    f => !addedKeys.value.has(f.attribute_key)
  );
});

// 搜索过滤 - 可添加字段
const filteredUnaddedFields = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return unaddedFields.value;
  return unaddedFields.value.filter(field => {
    const name = (field.attribute_display_name || '').toLowerCase();
    const key = (field.attribute_key || '').toLowerCase();
    const desc = (field.attribute_description || '').toLowerCase();
    return name.includes(query) || key.includes(query) || desc.includes(query);
  });
});

// 临时选中的数量
const tempSelectedCount = computed(() => tempSelectedKeys.value.size);

// 是否达到上限
const isAtLimit = computed(
  () => props.selectedFields.length >= props.maxFields
);

// 还能添加多少个
const remainingSlots = computed(
  () => props.maxFields - props.selectedFields.length
);

// 全选 checkbox 的 ref
const selectAllCheckboxRef = ref(null);

// 是否全选了所有可选字段
const isSelectAllFull = computed(() => {
  if (filteredUnaddedFields.value.length === 0) return false;
  return tempSelectedCount.value === filteredUnaddedFields.value.length;
});

// 是否部分选中（indeterminate 状态）
const isSelectPartial = computed(() => {
  return tempSelectedCount.value > 0 && !isSelectAllFull.value;
});

// 监听选中状态，更新 checkbox 的 indeterminate 属性
watch([isSelectAllFull, isSelectPartial], () => {
  nextTick(() => {
    if (selectAllCheckboxRef.value) {
      selectAllCheckboxRef.value.indeterminate = isSelectPartial.value;
    }
  });
});

// 切换临时选中
const toggleTempSelection = key => {
  const selected = new Set(tempSelectedKeys.value);
  if (selected.has(key)) {
    selected.delete(key);
  } else {
    // 检查是否超过剩余名额
    if (selected.size < remainingSlots.value) {
      selected.add(key);
    } else {
      // 已达上限，提示用户
      useAlert(
        t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.LIMIT_REACHED', {
          max: props.maxFields,
        })
      );
    }
  }
  tempSelectedKeys.value = selected;
};

const isTempSelected = key => tempSelectedKeys.value.has(key);

// 全选/取消全选可添加字段
const selectAllUnadded = () => {
  // 如果没有可添加字段，提示用户
  if (filteredUnaddedFields.value.length === 0) {
    useAlert(
      t(
        'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.NO_MORE_FIELDS_TOAST'
      )
    );
    return;
  }

  // 如果已经全选，则取消全选
  if (isSelectAllFull.value) {
    tempSelectedKeys.value = new Set();
    return;
  }

  const keys = filteredUnaddedFields.value
    .slice(0, remainingSlots.value)
    .map(f => f.attribute_key);
  tempSelectedKeys.value = new Set(keys);

  // 如果可添加数量少于实际未添加数量，提示用户
  if (filteredUnaddedFields.value.length > remainingSlots.value) {
    useAlert(
      t('INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.LIMIT_REACHED', {
        max: props.maxFields,
      })
    );
  }
};

// 清空临时选择
const clearTempSelection = () => {
  tempSelectedKeys.value = new Set();
};

// 批量添加
const handleBatchAdd = () => {
  const keys = Array.from(tempSelectedKeys.value);
  if (keys.length > 0) {
    emit('batch-add', keys);
    closeDrawer();
  }
};

// 打开抽屉
const openDrawer = () => {
  searchQuery.value = '';
  tempSelectedKeys.value = new Set();
  showDrawer.value = true;
};

// 关闭抽屉
const closeDrawer = () => {
  showDrawer.value = false;
  searchQuery.value = '';
  tempSelectedKeys.value = new Set();
};

// 暴露方法供父组件调用
defineExpose({
  open: openDrawer,
  close: closeDrawer,
});
</script>

<template>
  <Teleport to="body">
    <!-- 遮罩层 -->
    <transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showDrawer"
        class="fixed inset-0 bg-black/50 z-[9998]"
        @click="closeDrawer"
      />
    </transition>

    <!-- 抽屉面板 -->
    <transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition-transform duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="showDrawer"
        class="fixed right-0 top-0 h-full w-[42rem] bg-n-solid-1 dark:bg-n-slate-900 shadow-2xl z-[9999] flex flex-col"
      >
        <!-- 头部 -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-slate-6 dark:border-n-slate-700"
        >
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-code" class="size-5 text-n-brand" />
            <h3 class="text-lg font-medium text-n-slate-12">
              配置 AI 可以收集和更新的线索字段
            </h3>
          </div>
          <button
            class="p-1 rounded-lg hover:bg-n-slate-2 dark:hover:bg-n-slate-800 transition-colors"
            @click="closeDrawer"
          >
            <Icon icon="i-lucide-x" class="size-5 text-n-slate-11" />
          </button>
        </div>

        <!-- 搜索框 -->
        <div class="px-6 py-3">
          <div class="relative">
            <Icon
              class="absolute size-4 left-2.5 top-1/2 -translate-y-1/2 text-n-slate-10"
              icon="i-lucide-search"
            />
            <input
              v-model="searchQuery"
              class="w-full h-8 pl-8 pr-3 text-sm bg-n-alpha-2 dark:bg-n-solid-1 border border-transparent rounded-lg outline-none focus:border-n-brand text-n-slate-12 placeholder:text-n-slate-10"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.SEARCH_PLACEHOLDER'
                )
              "
            />
          </div>
        </div>

        <!-- 内容区域 -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <!-- 可添加区域 -->
          <div class="mb-6">
            <!-- 全选按钮 -->
            <div
              v-if="!isAtLimit"
              class="flex items-center gap-2 mb-3 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-pointer"
              @click="selectAllUnadded"
            >
              <input
                ref="selectAllCheckboxRef"
                type="checkbox"
                class="size-4 rounded border-n-slate-4 pointer-events-none"
                :checked="isSelectAllFull"
              />
              <span>{{
                $t(
                  'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.SELECT_ALL'
                )
              }}</span>
            </div>

            <!-- 搜索无结果 -->
            <div
              v-if="searchQuery && filteredUnaddedFields.length === 0"
              class="flex flex-col items-center justify-center py-16 text-center"
            >
              <Icon
                icon="i-lucide-search-x"
                class="size-16 text-n-slate-7 dark:text-n-slate-4 mb-4"
              />
              <h3
                class="text-xl font-semibold text-n-slate-12 dark:text-n-slate-1 mb-2"
              >
                {{
                  $t(
                    'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.NO_RESULTS_TITLE'
                  )
                }}
              </h3>
              <p class="text-n-slate-11 dark:text-n-slate-5">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.NO_RESULTS_DESC'
                  )
                }}
              </p>
            </div>

            <!-- 达到上限 -->
            <div v-else-if="isAtLimit" class="py-8 text-center">
              <Icon
                icon="i-lucide-check-circle-2"
                class="size-8 text-green-500 mx-auto mb-2"
              />
              <p class="text-sm text-n-slate-9">
                {{
                  $t(
                    'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.LIMIT_REACHED',
                    { max: maxFields }
                  )
                }}
              </p>
            </div>

            <!-- 没有更多字段可添加 - 引导页 -->
            <div
              v-else-if="hasNoMoreFields"
              class="relative overflow-hidden rounded-lg"
            >
              <!-- 假数据 -->
              <div class="grid gap-3 opacity-50 pointer-events-none p-0.5">
                <div
                  v-for="field in placeholderFields"
                  :key="field.attribute_key"
                  class="flex gap-3 p-4 rounded-2xl outline outline-1 bg-n-solid-1 outline-n-container"
                >
                  <div class="flex-shrink-0 flex items-center text-n-slate-9">
                    <Icon icon="i-lucide-grip-vertical" class="size-4" />
                  </div>
                  <div class="flex-1 flex flex-col gap-2 min-w-0">
                    <div
                      class="flex flex-wrap gap-2 justify-between items-center"
                    >
                      <div class="flex flex-wrap gap-2 items-center min-w-0">
                        <span
                          class="text-sm font-medium truncate text-n-slate-12"
                        >
                          {{ field.attribute_display_name }}
                        </span>
                        <div class="w-px h-3 bg-n-strong" />
                        <div
                          class="flex gap-2 items-center text-sm text-n-slate-11"
                        >
                          <Icon
                            :icon="getTypeIcon(field.attribute_display_type)"
                            class="size-4"
                          />
                          <span class="text-sm">{{
                            field.attribute_display_type
                          }}</span>
                        </div>
                        <div class="w-px h-3 bg-n-weak" />
                        <div class="flex items-center gap-1.5 text-n-slate-11">
                          <Icon icon="i-lucide-key-round" class="size-4" />
                          <span class="line-clamp-1 text-sm">{{
                            field.attribute_key
                          }}</span>
                        </div>
                      </div>
                      <div class="flex gap-2 items-center">
                        <div class="w-px h-3 bg-n-strong" />
                        <div class="h-8 w-8 flex items-center justify-center">
                          <Icon
                            icon="i-lucide-circle"
                            class="size-5 text-n-slate-7"
                          />
                        </div>
                      </div>
                    </div>
                    <p
                      v-if="field.attribute_description"
                      class="mb-0 text-sm text-n-slate-11"
                    >
                      {{ field.attribute_description }}
                    </p>
                  </div>
                </div>
              </div>
              <!-- 渐变蒙层 + 提示 -->
              <div
                class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-n-surface-1 via-n-surface-1/80 to-transparent pt-8 pb-4"
              >
                <div class="flex flex-col items-center justify-center gap-6">
                  <div class="flex flex-col items-center justify-center gap-3">
                    <h2
                      class="text-3xl font-medium text-center text-n-slate-12"
                    >
                      没有更多字段了
                    </h2>
                    <p
                      class="text-base text-center text-n-slate-11 tracking-[0.3px]"
                    >
                      所有可用字段都已添加，如需更多字段请先创建
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 可添加列表 -->
            <div v-else class="grid gap-3">
              <div
                v-for="field in filteredUnaddedFields"
                :key="field.attribute_key"
                class="flex gap-3 p-4 rounded-2xl outline outline-1 transition-all cursor-pointer"
                :class="
                  isTempSelected(field.attribute_key)
                    ? 'outline-n-brand bg-n-brand/5'
                    : 'bg-n-solid-1 outline-n-container hover:outline-n-slate-6'
                "
                @click="toggleTempSelection(field.attribute_key)"
              >
                <div class="flex-shrink-0 flex items-center text-n-slate-9">
                  <Icon icon="i-lucide-grip-vertical" class="size-4" />
                </div>
                <div class="flex-1 flex flex-col gap-2 min-w-0">
                  <div
                    class="flex flex-wrap gap-2 justify-between items-center"
                  >
                    <div class="flex flex-wrap gap-2 items-center min-w-0">
                      <span
                        class="text-sm font-medium truncate text-n-slate-12"
                      >
                        {{ field.attribute_display_name }}
                      </span>
                      <div class="w-px h-3 bg-n-strong" />
                      <div
                        class="flex gap-2 items-center text-sm text-n-slate-11"
                      >
                        <Icon
                          :icon="getTypeIcon(field.attribute_display_type)"
                          class="size-4"
                        />
                        <span class="text-sm">{{
                          field.attribute_display_type
                        }}</span>
                      </div>
                      <div class="w-px h-3 bg-n-weak" />
                      <div class="flex items-center gap-1.5 text-n-slate-11">
                        <Icon icon="i-lucide-key-round" class="size-4" />
                        <span class="line-clamp-1 text-sm">{{
                          field.attribute_key
                        }}</span>
                      </div>
                    </div>
                    <div class="flex gap-2 items-center">
                      <div class="w-px h-3 bg-n-strong" />
                      <div class="h-8 w-8 flex items-center justify-center">
                        <Icon
                          :icon="
                            isTempSelected(field.attribute_key)
                              ? 'i-lucide-check-circle-2'
                              : 'i-lucide-circle'
                          "
                          class="size-5"
                          :class="
                            isTempSelected(field.attribute_key)
                              ? 'text-n-brand'
                              : 'text-n-slate-7'
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <p
                    v-if="field.attribute_description"
                    class="mb-0 text-sm text-n-slate-11"
                  >
                    {{ field.attribute_description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div
          class="flex items-center gap-3 px-6 py-4 border-t border-n-slate-6 dark:border-n-slate-700"
        >
          <Button
            variant="faded"
            color="slate"
            :label="
              $t(
                'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.CANCEL'
              )
            "
            class="flex-1"
            @click="closeDrawer"
          />
          <Button
            :label="
              $t(
                'INTEGRATION_SETTINGS.MIP.UPDATE_LEADS_TOOL.ADD_FIELD_DRAWER.ADD_SELECTED',
                { count: tempSelectedCount }
              )
            "
            :disabled="tempSelectedCount === 0"
            class="flex-1"
            @click="handleBatchAdd"
          />
        </div>
      </div>
    </transition>
  </Teleport>
</template>
